import { AlertCircle, CheckCircle2, FileText, Loader2, Upload, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { type DocumentRequestInfo, useUploadFiles } from '../api/upload';

/** Allowed file types and their extensions */
const ALLOWED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES_PER_TYPE = 5;

interface FileUploadFormProps {
  token: string;
  documentRequests: DocumentRequestInfo[];
  onSuccess?: () => void;
}

interface SelectedFile {
  file: File;
  preview?: string;
  error?: string;
}

/** Format document type for display */
function formatDocumentType(type: string, otherDescription?: string | null): string {
  if (type === 'OTHER' && otherDescription) {
    return `Other: ${otherDescription}`;
  }
  return type
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Format file size for display */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Single document type upload section
 */
interface DocumentUploadSectionProps {
  token: string;
  documentRequest: DocumentRequestInfo;
  onUploadSuccess: () => void;
  isUploading: boolean;
  setIsUploading: (uploading: boolean) => void;
}

function DocumentUploadSection({
  token,
  documentRequest,
  onUploadSuccess,
  isUploading,
  setIsUploading,
}: DocumentUploadSectionProps) {
  const [files, setFiles] = useState<SelectedFile[]>([]);
  const [uploaded, setUploaded] = useState(false);

  const uploadMutation = useUploadFiles(() => {
    setUploaded(true);
    setFiles([]);
    setIsUploading(false);
    onUploadSuccess();
  });

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    const newFiles: SelectedFile[] = acceptedFiles.map((file) => {
      const errors: string[] = [];

      if (file.size > MAX_FILE_SIZE) {
        errors.push(`File exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`);
      }

      if (!Object.keys(ALLOWED_TYPES).includes(file.type)) {
        errors.push('File type not allowed');
      }

      return {
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        error: errors.length > 0 ? errors.join('. ') : undefined,
      };
    });

    const rejectedWithErrors: SelectedFile[] = rejectedFiles.map(({ file, errors }) => ({
      file,
      error: errors.map((e) => e.message).join('. '),
    }));

    setFiles((prev) => {
      const combined = [...prev, ...newFiles, ...rejectedWithErrors];
      return combined.slice(0, MAX_FILES_PER_TYPE);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: ALLOWED_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES_PER_TYPE - files.length,
    disabled: files.length >= MAX_FILES_PER_TYPE || uploadMutation.isPending || isUploading,
  });

  const removeFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleUpload = () => {
    const validFiles = files.filter((f) => !f.error).map((f) => f.file);
    if (validFiles.length === 0) return;

    setIsUploading(true);
    uploadMutation.mutate({
      token,
      documentRequestId: documentRequest.id,
      files: validFiles,
    });
  };

  const validFileCount = files.filter((f) => !f.error).length;
  const canUpload = validFileCount > 0 && !uploadMutation.isPending && !isUploading;

  // Show success state if already received or just uploaded
  if (documentRequest.received || uploaded) {
    return (
      <Card className="border-green-200 bg-green-50/50 h-full flex flex-col">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            <span className="truncate">
              {formatDocumentType(documentRequest.documentType, documentRequest.otherDescription)}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center">
          <p className="text-sm text-green-700">Document received</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Upload className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <span className="truncate">
            {formatDocumentType(documentRequest.documentType, documentRequest.otherDescription)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 flex-1 flex flex-col">
        {/* Dropzone */}
        <div className={cn('flex-1', files.length === 0 && 'min-h-[120px]')}>
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors h-full flex flex-col items-center justify-center',
              isDragActive && !isDragReject && 'border-primary bg-primary/5',
              isDragReject && 'border-destructive bg-destructive/5',
              !isDragActive && 'border-muted-foreground/25 hover:border-primary/50',
              (files.length >= MAX_FILES_PER_TYPE || uploadMutation.isPending || isUploading) &&
                'opacity-50 cursor-not-allowed',
            )}
          >
            <input {...getInputProps()} />
            <Upload className="h-6 w-6 mb-2 text-muted-foreground" />
            {isDragActive ? (
              isDragReject ? (
                <p className="text-destructive text-sm">File type not allowed</p>
              ) : (
                <p className="text-primary text-sm">Drop files here...</p>
              )
            ) : (
              <>
                <p className="text-sm mb-1">Drag & drop or click to browse</p>
                <p className="text-xs text-muted-foreground">
                  PDF, JPG, PNG, GIF, WebP (max {MAX_FILE_SIZE / 1024 / 1024}MB)
                </p>
              </>
            )}
          </div>
        </div>

        {/* Selected files list */}
        {files.length > 0 && (
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {files.map((f, index) => (
              <div
                key={`${f.file.name}-${index}`}
                className={cn(
                  'flex items-center gap-2 p-2 rounded-lg border text-sm',
                  f.error ? 'bg-destructive/5 border-destructive/30' : 'bg-muted/50',
                )}
              >
                {f.preview ? (
                  <img src={f.preview} alt={f.file.name} className="h-8 w-8 object-cover rounded" />
                ) : (
                  <FileText className="h-8 w-8 text-muted-foreground p-1 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{f.file.name}</div>
                  <div className="text-xs text-muted-foreground">{formatFileSize(f.file.size)}</div>
                  {f.error && (
                    <div className="flex items-center gap-1 text-xs text-destructive">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{f.error}</span>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0"
                  onClick={() => removeFile(index)}
                  disabled={uploadMutation.isPending || isUploading}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        {files.length > 0 && (
          <Button
            type="button"
            size="sm"
            className="w-full mt-auto"
            disabled={!canUpload}
            onClick={handleUpload}
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-3 w-3" />
                Upload {validFileCount} file{validFileCount !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Progress indicator component
 */
function UploadProgressIndicator({ total, completed }: { total: number; completed: number }) {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-foreground">Upload Progress</span>
        <span className="text-sm text-muted-foreground">
          {completed} of {total} document{total !== 1 ? 's' : ''} uploaded
        </span>
      </div>
      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-500 ease-out rounded-full"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Main file upload form - shows separate sections for each document type
 */
export function FileUploadForm({ token, documentRequests, onSuccess }: FileUploadFormProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);

  const pendingRequests = documentRequests.filter((doc) => !doc.received);
  const receivedRequests = documentRequests.filter((doc) => doc.received);
  const totalPending = pendingRequests.length;
  const totalReceived = receivedRequests.length + completedCount;
  const totalDocuments = documentRequests.length;

  const handleUploadSuccess = () => {
    const newCount = completedCount + 1;
    setCompletedCount(newCount);

    // If all documents have been uploaded, trigger the overall success callback
    if (newCount >= totalPending) {
      onSuccess?.();
    }
  };

  if (documentRequests.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No documents have been requested.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <UploadProgressIndicator total={totalDocuments} completed={totalReceived} />

      {/* Instructions */}
      <p className="text-sm text-muted-foreground">
        Please upload the requested documents below. You can upload multiple files for each document
        type.
      </p>

      {/* Document upload grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {documentRequests.map((doc) => (
          <DocumentUploadSection
            key={doc.id}
            token={token}
            documentRequest={doc}
            onUploadSuccess={handleUploadSuccess}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
          />
        ))}
      </div>
    </div>
  );
}
